import { useState, useCallback } from 'react';

export interface ErrorState {
  uploadErrors: string[];
  validationErrors: string[];
  systemErrors: string[];
  hasErrors: boolean;
}

export interface ErrorActions {
  addUploadError: (error: string) => void;
  addValidationError: (error: string) => void;
  addSystemError: (error: string) => void;
  removeError: (type: 'upload' | 'validation' | 'system', index: number) => void;
  clearErrors: (type?: 'upload' | 'validation' | 'system' | 'all') => void;
  handleError: (error: Error, context?: string) => void;
}

export interface UseErrorManagementReturn extends ErrorState, ErrorActions {}

/**
 * Hook untuk menangani Error Management dengan isolasi penuh
 * Sesuai dengan todo: "Buat modul error management terpisah (useErrorManagement.ts) untuk error handling"
 * 
 * Tanggung jawab:
 * - Centralized error state management
 * - Error categorization (upload, validation, system)
 * - Error logging dan reporting
 * - Error recovery mechanisms
 * 
 * TIDAK menangani:
 * - File validation (handled by useUploadValidation)
 * - Upload logic (handled by useFileProcessing)
 * - AI Settings (handled by useAISettings)
 */
export const useErrorManagement = (): UseErrorManagementReturn => {
  // Error states - HANYA untuk error management
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [systemErrors, setSystemErrors] = useState<string[]>([]);

  // Computed state
  const hasErrors = uploadErrors.length > 0 || validationErrors.length > 0 || systemErrors.length > 0;

  // Add error functions
  const addUploadError = useCallback((error: string) => {
    setUploadErrors(prev => {
      // Prevent duplicate errors
      if (prev.includes(error)) return prev;
      return [...prev, error];
    });
  }, []);

  const addValidationError = useCallback((error: string) => {
    setValidationErrors(prev => {
      // Prevent duplicate errors
      if (prev.includes(error)) return prev;
      return [...prev, error];
    });
  }, []);

  const addSystemError = useCallback((error: string) => {
    setSystemErrors(prev => {
      // Prevent duplicate errors
      if (prev.includes(error)) return prev;
      return [...prev, error];
    });
    
    // Log system errors for debugging
    console.error('System Error:', error);
  }, []);

  // Remove specific error
  const removeError = useCallback((type: 'upload' | 'validation' | 'system', index: number) => {
    switch (type) {
      case 'upload':
        setUploadErrors(prev => prev.filter((_, i) => i !== index));
        break;
      case 'validation':
        setValidationErrors(prev => prev.filter((_, i) => i !== index));
        break;
      case 'system':
        setSystemErrors(prev => prev.filter((_, i) => i !== index));
        break;
    }
  }, []);

  // Clear errors
  const clearErrors = useCallback((type?: 'upload' | 'validation' | 'system' | 'all') => {
    switch (type) {
      case 'upload':
        setUploadErrors([]);
        break;
      case 'validation':
        setValidationErrors([]);
        break;
      case 'system':
        setSystemErrors([]);
        break;
      case 'all':
      default:
        setUploadErrors([]);
        setValidationErrors([]);
        setSystemErrors([]);
        break;
    }
  }, []);

  // Generic error handler with context
  const handleError = useCallback((error: Error, context?: string) => {
    const errorMessage = context 
      ? `${context}: ${error.message}`
      : error.message;

    // Categorize error based on context or error type
    if (context) {
      switch (context.toLowerCase()) {
        case 'upload':
        case 'file upload':
        case 'upload process':
          addUploadError(errorMessage);
          break;
        case 'validation':
        case 'file validation':
        case 'input validation':
          addValidationError(errorMessage);
          break;
        default:
          addSystemError(errorMessage);
          break;
      }
    } else {
      // Try to categorize based on error message
      const message = error.message.toLowerCase();
      if (message.includes('upload') || message.includes('file')) {
        addUploadError(errorMessage);
      } else if (message.includes('validation') || message.includes('invalid')) {
        addValidationError(errorMessage);
      } else {
        addSystemError(errorMessage);
      }
    }

    // Log error for debugging
    console.error('Error handled:', {
      error: error.message,
      context,
      stack: error.stack
    });
  }, [addUploadError, addValidationError, addSystemError]);

  return {
    // State
    uploadErrors,
    validationErrors,
    systemErrors,
    hasErrors,
    
    // Actions
    addUploadError,
    addValidationError,
    addSystemError,
    removeError,
    clearErrors,
    handleError
  };
};
